import React, { useState, useEffect, useContext, useRef } from "react";
import AppContext from "../../contexts/AppContext";
import { Line } from "react-chartjs-2";
import "react-datepicker/dist/react-datepicker.css";
import "chart.js/auto";
import dayjs from 'dayjs';
import moment from "moment";
import { FaChevronDown, FaDownload } from "react-icons/fa";
import { hasElevatedSessionAccess } from "../../utils/sessionPermissions";
import useReportExport, { EXPORT_PROBLEMS } from "./useReportExport";

// The names the range menu shows, so the PDF can say which period was chosen.
const RANGE_LABELS = { 7: "Past 7 Days", 14: "Past 14 Days", 30: "Past 30 Days", 90: "Past 3 Months", 180: "Past 6 Months", 365: "Past 1 Year" };

// Browsing time is stored in minutes, which stops being readable once it passes an hour.
const formatDuration = (minutes) => {
    const total = Math.round(Number(minutes) || 0);
    if (total < 60) return `${total} min`;
    const hours = Math.floor(total / 60).toLocaleString("en-AU");
    return total % 60 ? `${hours} h ${total % 60} min` : `${hours} h`;
};

// Draws a dashed vertical line and a label on the day the venue was created.
const venueCreatedMarker = {
    id: "venueCreated",
    afterDatasetsDraw(chart, _args, options) {
        if (!options || options.index < 0) return;
        const { ctx, chartArea, scales } = chart;
        const x = scales.x.getPixelForValue(options.index);
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = "#4a63ff";
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = "#4a63ff";
        ctx.font = "12px sans-serif";
        ctx.textAlign = x > chartArea.right - 120 ? "right" : "left";
        ctx.fillText(options.label, x + (ctx.textAlign === "right" ? -6 : 6), chartArea.top + 12);
        ctx.restore();
    },
};

const SpotTrafficDashboard = () => {
    const { selectedRole, token, userInfo } = useContext(AppContext);
    const [trendData, setTrendData] = useState([]);
    const [venueCreatedDate, setVenueCreatedDate] = useState(null);
    const [summary, setSummary] = useState(null);
    const [deviceDays, setDeviceDays] = useState([]);
    const reportRef = useRef(null);
    const figuresOf = useRef(null); // the venue the figures on screen belong to

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const sevenDaysAgo = new Date(yesterday);
    sevenDaysAgo.setDate(yesterday.getDate() - 6);
    const [startDate, setStartDate] = useState(sevenDaysAgo);
    const [endDate, setEndDate] = useState(yesterday);
    const [loading, setLoading] = useState(false);
    const [timeRange, setTimeRange] = useState("7"); // 默认为过去 7 天

    const [maxTicks, setMaxTicks] = useState(window.innerWidth < 640 ? 4 : 20);
    // The venue and period the figures on screen belong to: the export checks them before and after it works, so a
    // picture is never made of one venue or period under the heading of another.
    const { exporting, problem, exportPdf, markChanged } = useReportExport({ screenKey: `${selectedRole?.id}|${timeRange}|${startDate.getTime()}|${endDate.getTime()}`, loading });

    const roleArray = userInfo?.roleInfo?.[selectedRole?.type] || [];
    const currentRole = roleArray.find((r) => r.id === selectedRole?.id);
    const permissionLevel = currentRole?.permissionLevel;
    const canShowAdvanced = hasElevatedSessionAccess(userInfo, permissionLevel);

    useEffect(() => {
        const handleResize = () => {
            setMaxTicks(window.innerWidth < 640 ? 4 : 20);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleTimeRangeChange = (e) => {
        const range = e.target.value;
        setTimeRange(range);

        const newEndDate = new Date(yesterday);
        let newStartDate = new Date(newEndDate);

        if (range === "7") {
            newStartDate.setDate(newEndDate.getDate() - 6);
        } else if (range === "14") {
            newStartDate.setDate(newEndDate.getDate() - 13);
        } else if (range === "30") {
            newStartDate.setMonth(newEndDate.getMonth() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "90") {
            newStartDate.setMonth(newEndDate.getMonth() - 3);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "180") {
            newStartDate.setMonth(newEndDate.getMonth() - 6);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "365") {
            newStartDate.setFullYear(newEndDate.getFullYear() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
        }
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    };


    useEffect(() => {
        // Set when the venue or period changes (or the page closes): an answer that comes back after that belongs to
        // the old choice and must not replace the figures of the new one.
        let outdated = false;
        const fetchTrafficData = async () => {
            try {
                setLoading(true);
                // Another venue was chosen: its figures are on their way, so the last venue's must not stay on screen meanwhile.
                if (figuresOf.current !== null && figuresOf.current !== selectedRole?.id) {
                    figuresOf.current = null;
                    setTrendData([]);
                    setVenueCreatedDate(null);
                    setSummary(null);
                    setDeviceDays([]);
                }

                const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (!selectedRole || !userTimeZone) {
                    throw new Error("Missing spotId or userTimeZone");
                }

                // 1. 先复制一下 startDate / endDate，免得直接修改它们的 state
                const localStart = new Date(startDate);
                const localEnd = new Date(endDate);

                // 2. 把开始日期修正到当日 00:00:00.000
                localStart.setHours(0, 0, 0, 0);

                // 3. 把结束日期修正到当日 23:59:59.999
                localEnd.setHours(23, 59, 59, 999);

                const request = (path) => fetch(`${process.env.REACT_APP_API_BASE_URL}/private/analysis/venue/read/${path}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        venue_id: selectedRole.id,
                        startDate: dayjs(localStart).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        endDate: dayjs(localEnd).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        userTimeZone,
                    }),
                });

                const [response, deviceResponse] = await Promise.all([
                    request("traffic/and/region"),
                    // The device split is extra: if it cannot be read, the rest of the page still works.
                    request("device/usage").catch(() => null),
                ]);

                const data = await response.json();
                const deviceData = deviceResponse && deviceResponse.ok ? await deviceResponse.json().catch(() => null) : null;
                if (outdated) return;

                markChanged();
                figuresOf.current = selectedRole.id;
                setTrendData(data.trend || []);
                setVenueCreatedDate(data.venueCreatedDate || null);
                setSummary(data.summary || null);
                setDeviceDays(Array.isArray(deviceData) ? deviceData : []);
            } catch (error) {
                if (outdated) return;
                console.error("Failed to fetch data:", error);
                // Figures of the previous venue or period must not stay on screen under the new choice.
                setTrendData([]);
                setVenueCreatedDate(null);
                setSummary(null);
                setDeviceDays([]);
                figuresOf.current = null;
            } finally {
                if (!outdated) setLoading(false);
            }
        };

        fetchTrafficData();
        return () => { outdated = true; };
    }, [startDate, endDate, selectedRole, token, markChanged]); // 确保依赖项正确设置

    // Days before the venue was created have no data, which is not the same as zero minutes.
    // A day that does have visits is always shown, even if it is dated before the creation day.
    const isEmptyBeforeCreation = (item) => !!venueCreatedDate && item.date < venueCreatedDate && !item.totalDuration;
    const usedDays = trendData.filter((item) => !isEmptyBeforeCreation(item));
    const createdIndex = trendData.findIndex((item) => item.date === venueCreatedDate);
    const createdLabel = venueCreatedDate ? moment(venueCreatedDate, "YYYY-MM-DD").format("D MMM YYYY") : "";
    const totalMinutes = usedDays.reduce((sum, item) => sum + Number(item.totalDuration || 0), 0);
    const busiestDay = usedDays.reduce((best, item) => (!best || item.totalDuration > best.totalDuration ? item : best), null);
    const hasBusiestDay = !!busiestDay && busiestDay.totalDuration > 0;
    // Share of visits per device type over the whole period.
    const deviceCounts = {};
    deviceDays.forEach((day) => (day.statistics?.device || []).forEach(({ type, amount }) => {
        deviceCounts[type] = (deviceCounts[type] || 0) + Number(amount || 0);
    }));
    const deviceTotal = Object.values(deviceCounts).reduce((sum, amount) => sum + amount, 0);
    const devices = Object.entries(deviceCounts)
        .map(([type, amount]) => ({ type, percent: deviceTotal ? (amount / deviceTotal) * 100 : 0 }))
        .sort((a, b) => b.percent - a.percent);

    const summaryStats = [
        ...(summary ? [
            ["Average per visit", summary.visits ? formatDuration(totalMinutes / summary.visits) : "–", "Total browsing time divided by the number of visits."],
        ] : []),
        ["Total browsing time", formatDuration(totalMinutes), "All visits to your page in this period, added together."],
        ["Average per day", formatDuration(usedDays.length ? totalMinutes / usedDays.length : 0), "Total browsing time divided by the number of days in use."],
        ["Busiest day", hasBusiestDay ? moment(busiestDay.date, "YYYY-MM-DD").format("ddd D MMM") : "–", hasBusiestDay ? `${formatDuration(busiestDay.totalDuration)} that day` : "No visits in this period."],
    ];

    // Saves what is on screen (the figures, the devices and the chart, and the content ranking under them) as a PDF, for the
    // period chosen in the menu, under a heading that names the venue and the dates.
    const downloadReport = () => exportPdf({
        element: reportRef.current,
        withExtras: true,
        venueName: currentRole?.alias || currentRole?.name || "Venue",
        reportName: "browsing report",
        rangeText: `${RANGE_LABELS[timeRange] || "Selected period"}: ${moment(startDate).format("D MMM YYYY")} - ${moment(endDate).format("D MMM YYYY")}`,
        filename: `browsing-report-${moment(startDate).format("YYYY-MM-DD")}-to-${moment(endDate).format("YYYY-MM-DD")}.pdf`,
    });

    const renderDevices = () => (
        <div data-print-block="true" className="rounded-xl border border-[#e2e7f6] bg-white p-4">
            <p className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">Devices</p>
            <p className="mt-1 text-sm text-gray-500">What guests used to open your page.</p>
            <ul className="mt-3 space-y-2">
                {devices.map((device) => (
                    <li key={device.type} className="flex items-center gap-3 text-sm text-[#24324d]">
                        <span className="w-28 shrink-0">{device.type}</span>
                        <span className="h-2 flex-1 overflow-hidden rounded-full bg-[#eef2f9]">
                            <span className="block h-full rounded-full bg-[#4a63ff]" style={{ width: `${device.percent}%` }} />
                        </span>
                        <span className="w-12 shrink-0 text-right font-medium">{Math.round(device.percent)}%</span>
                    </li>
                ))}
            </ul>
        </div>
    );

    const renderSummary = () => (
        <div className={`grid grid-cols-1 gap-4 ${summaryStats.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
            {summaryStats.map(([title, value, hint]) => (
                <div key={title} data-print-block="true" className="rounded-xl border border-[#e2e7f6] bg-white p-4">
                    <p className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">{title}</p>
                    <p className="mt-1 text-2xl font-semibold text-[#24324d]">{value}</p>
                    <p className="mt-1 text-sm text-gray-500">{hint}</p>
                </div>
            ))}
        </div>
    );

    const renderTrendChart = () => {
        const labels = trendData.map((item) => item.date);
        const durations = trendData.map((item) => (isEmptyBeforeCreation(item) ? null : item.totalDuration));
        // Plot in hours once any day passes two hours; the tooltip always spells out hours and minutes.
        const inHours = Math.max(0, ...durations.filter((value) => value !== null)) >= 120;
        const plotted = durations.map((value) => (value === null ? null : inHours ? Number((value / 60).toFixed(2)) : value));

        const data = {
            labels,
            datasets: [
                {
                    label: "Browsing time",
                    data: plotted,
                    borderColor: "rgba(75,192,192,1)",
                    backgroundColor: "rgba(75,192,192,0.2)",
                },
            ],
        };

        const options = {
            responsive: true,
            maintainAspectRatio: false, // 宽高比不固定
            plugins: {
                venueCreated: { index: createdIndex, label: `Venue created ${createdLabel}` },
                datalabels: {
                    display: false,
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (context) => `${data.datasets[0].label}: ${formatDuration(durations[context.dataIndex])}`,
                    },
                },
            },
            scales: {
                x: {
                    title: {
                        display: true, // 显示标题
                        text: 'Time (Date)', // x轴单位
                        font: {
                            size: 14, // 设置字体大小
                            weight: 'bold', // 字体加粗
                        },
                        color: '#4B5563', // 设置标题颜色
                    },
                    ticks: {
                        maxTicksLimit: maxTicks, // 限制最多显示的刻度
                        callback: function (value, index, ticks) {
                            const rawDateStr = this.getLabelForValue(value);
                            const dateObj = moment(rawDateStr, "YYYY-MM-DD");
                            return dateObj.format("MMM D");
                        },
                    },
                },
                y: {
                    title: {
                        display: true, // 显示标题
                        text: inHours ? 'Hours' : 'Minutes', // y轴单位
                        font: {
                            size: 14, // 设置字体大小
                            weight: 'bold', // 字体加粗
                        },
                        color: '#4B5563', // 设置标题颜色
                    },
                },
            },
        };



        return (
            <div className="w-full h-[35vh] md:h-[35vh]">
                {loading ? (
                    <p className="text-lg font-semibold text-gray-500">Loading...</p>
                ) : (
                    <Line data={data} options={options} plugins={[venueCreatedMarker]} />
                )}
            </div>
        );
    };
    return (
        <div ref={reportRef} className="relative flex flex-col gap-4 p-6 bg-gray-50 rounded-lg shadow-lg overflow-visible">

            <div data-print-block="true" className="flex gap-4 items-center">

                {/* 时间范围下拉菜单 */}
                <label className="block">
                    <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                        Time Range
                    </span>
                    <div className="relative mt-2 inline-flex items-center">
                        <select
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            disabled={exporting}
                            className="h-11 w-44 appearance-none rounded-xl border border-[#dbe3f5] bg-white py-2 pl-4 pr-10 text-[15px] font-medium text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
                        >
                            <option value="7">Past 7 Days</option>
                            <option value="14">Past 14 Days</option>
                            <option value="30">Past 30 Days</option>
                            {canShowAdvanced && (
                                <>
                                    <option value="90">Past 3 Months</option>
                                    <option value="180">Past 6 Months</option>
                                    <option value="365">Past 1 Year</option>
                                </>
                            )}
                        </select>
                        <span className="pointer-events-none absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff]">
                            <FaChevronDown className="h-3 w-3" />
                        </span>
                    </div>
                </label>

                {problem && <span data-html2canvas-ignore="true" role="alert" className="ml-auto mt-6 text-sm text-red-600">{EXPORT_PROBLEMS[problem]}</span>}
                <button
                    data-html2canvas-ignore="true"
                    type="button"
                    onClick={downloadReport}
                    disabled={loading || exporting || trendData.length === 0}
                    className={`${problem ? "" : "ml-auto "}mt-6 inline-flex h-11 items-center gap-2 rounded-xl border border-[#dbe3f5] bg-white px-4 text-[15px] font-medium text-[#24324d] transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] disabled:cursor-not-allowed disabled:opacity-50`}
                >
                    <FaDownload className="h-3.5 w-3.5 text-[#4a63ff]" />
                    {exporting ? "Preparing PDF…" : "Download PDF"}
                </button>
            </div>

            {trendData.length > 0 && renderSummary()}
            {devices.length > 0 && renderDevices()}
            <p data-print-block="true" className="text-sm text-gray-500">
                Figures run up to yesterday. Today's visits appear here tomorrow.
                {venueCreatedDate && ` This venue was created on ${createdLabel}.`}
                {venueCreatedDate && trendData.length > 0 && trendData[0].date < venueCreatedDate && " Earlier days have no data."}
            </p>

            <div data-print-block="true" className="h-auto w-full mb-4">
                <h3 className="text-l font-semibold mb-2 text-center flex items-center justify-center">
                    Browsing Duration
                    <span className="ml-2 relative group cursor-pointer">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-700 text-sm font-bold">
                            ?
                        </span>
                        {/* Tooltip */}
                        <div className="absolute hidden group-hover:block bg-white text-gray-700 text-sm p-2 rounded shadow-md whitespace-normal w-[400px] -top-10 left-3/4 transform translate-x-[10%] text-justify">
                            This chart shows how long your page was open each day, added up across all visits
                        </div>
                    </span>
                </h3>

                <div className="h-auto w-full">
                    {trendData.length > 0 ? renderTrendChart() :
                        <p className="text-lg font-semibold text-gray-500 text-center">
                            No trend data available for the selected time range.
                        </p>}
                </div>
            </div>
        </div>
    );
};

export default SpotTrafficDashboard;
